import { Clock } from 'lucide-react'

import { Label } from 'primitives/label'
import React from 'react'
import { TimePeriodSelect } from './period-select'
import { TimePickerInput } from './time-picker-input'
import type { Period, TimePickerFormat } from './time-picker-utils'

interface TimePickerDemoProps {
  date: Date | undefined
  setDate: (date: Date | undefined) => void
  showSeconds?: boolean
  timeFormat?: TimePickerFormat
}

const getPeriod = (date: Date | undefined) => {
  if (!date) return 'PM'
  return date.getHours() >= 12 ? 'PM' : 'AM'
}

export const TimePicker = ({
  date,
  setDate,
  showSeconds = false,
  timeFormat = '12hours',
}: TimePickerDemoProps) => {
  const [period, setPeriod] = React.useState<Period>(getPeriod(date))

  const minuteRef = React.useRef<HTMLInputElement>(null)
  const hourRef = React.useRef<HTMLInputElement>(null)
  const secondRef = React.useRef<HTMLInputElement>(null)
  const periodRef = React.useRef<HTMLButtonElement>(null)

  return (
    <div className="flex items-end gap-2">
      <div className="grid gap-1 text-center">
        <Label htmlFor="hours" className="font-inter text-xs-500 normal-case tracking-normal">
          Hours
        </Label>
        <TimePickerInput
          picker={timeFormat}
          period={period}
          date={date}
          setDate={setDate}
          ref={hourRef}
          onRightFocus={() => minuteRef.current?.focus()}
        />
      </div>
      <div className="grid gap-1 text-center">
        <Label htmlFor="minutes" className="font-inter text-xs-500 normal-case tracking-normal">
          Minutes
        </Label>
        <TimePickerInput
          picker="minutes"
          date={date}
          setDate={setDate}
          ref={minuteRef}
          onLeftFocus={() => hourRef.current?.focus()}
          onRightFocus={() => secondRef.current?.focus()}
        />
      </div>
      {showSeconds && (
        <div className="grid gap-1 text-center">
          <Label htmlFor="seconds" className="font-inter text-xs-500 normal-case tracking-normal">
            Seconds
          </Label>
          <TimePickerInput
            picker="seconds"
            date={date}
            setDate={setDate}
            ref={secondRef}
            onLeftFocus={() => minuteRef.current?.focus()}
            onRightFocus={() => periodRef.current?.focus()}
          />
        </div>
      )}
      {timeFormat === '12hours' ? (
        <div className="grid gap-1 text-center">
          <Label htmlFor="period" className="font-inter text-xs-500 normal-case tracking-normal">
            Period
          </Label>
          <TimePeriodSelect
            period={period}
            setPeriod={setPeriod}
            date={date}
            setDate={setDate}
            ref={periodRef}
            onLeftFocus={() => secondRef.current?.focus()}
          />
        </div>
      ) : (
        <div className="self-end flex h-10 items-center">
          <Clock className="ml-2 h-4 w-4" />
        </div>
      )}
    </div>
  )
}
